import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { AppErrorFallback } from './AppErrorFallback';

const CHUNK_RELOAD_KEY = 'chunk_reload_attempted';

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes('importing a module script failed') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module')
  );
}

export function RouteErrorBoundary() {
  const error = useRouteError();

  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    if (chunkError) {
      // Guard against reload loops — only auto-reload once per session
      if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
        window.location.reload();
      }
      return;
    }
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);

    let errorObj: Error;
    if (isRouteErrorResponse(error)) {
      errorObj = new Error(`${error.status} ${error.statusText}: ${error.data}`);
    } else if (error instanceof Error) {
      errorObj = error;
    } else if (typeof error === 'string') {
      errorObj = new Error(error);
    } else {
      errorObj = new Error('An unexpected error occurred');
    }
    Sentry.captureException(errorObj);
  }, []);

  if (chunkError) return null;

  let errorObj: Error;
  if (isRouteErrorResponse(error)) {
    errorObj = new Error(`${error.status} ${error.statusText}: ${error.data}`);
  } else if (error instanceof Error) {
    errorObj = error;
  } else if (typeof error === 'string') {
    errorObj = new Error(error);
  } else {
    errorObj = new Error('An unexpected error occurred');
  }

  return (
    <AppErrorFallback
      resetError={() => window.location.reload()}
      error={errorObj}
    />
  );
}
